import { apiGetAsUser, apiPatchAsUser, apiPostAsUser } from './api_v2';

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 60_000;

async function readEnvelope(response) {
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok || !body?.ok) {
    const error = new Error(`request-failed-${response.status}`);
    error.status = response.status;
    error.validations = body?.validations ?? null;
    throw error;
  }
  return body.data ?? null;
}

async function createUserFlexiconAttachment(file, signal) {
  const response = await apiPostAsUser(
    '/media_attachments',
    {
      media_attachment: {
        purpose: 'user_flexicon',
        file_name: file.name || null,
        content_type: file.type || null,
        content_length: file.size || null,
      },
    },
    { signal },
  );

  const payload = await readEnvelope(response);
  if (!payload?.media_attachment?.presigned_upload_url) throw new Error('missing-presigned-url');
  return payload.media_attachment;
}

// Strip control chars + non-Latin-1, then escape the two characters that have meaning
// inside the quoted-string form of Content-Disposition (RFC 6266 §4.1).
function sanitizeFileName(name) {
  const trimmed = name?.trim() || 'avatar';
  return trimmed
    .replace(/[^ -ÿ]/gu, '_')
    .replace(/[\\"]/g, '_');
}

function uploadFileToPresignedUrl(presignedUrl, file, onProgress, signal) {
  return new Promise(function uploadFilePromiseExecutor(resolve, reject) {
    if (signal?.aborted) {
      reject(new Error('upload-aborted'));
      return;
    }

    const xhr = new XMLHttpRequest();

    function onSignalAbort() {
      xhr.abort();
    }

    xhr.upload.addEventListener('progress', function onProgressEventCallback(event) {
      if (!event.lengthComputable) return;
      const percent = Math.round((event.loaded / event.total) * 100);
      onProgress?.(Math.min(100, Math.max(0, percent)));
    });

    xhr.addEventListener('loadend', function onLoadEndCallback() {
      signal?.removeEventListener('abort', onSignalAbort);
    });

    xhr.addEventListener('load', function onLoadCallback() {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`upload-failed-${xhr.status}`));
      }
    });

    xhr.addEventListener('error', function onErrorCallback() {
      reject(new Error('upload-network-error'));
    });

    xhr.addEventListener('abort', function onAbortCallback() {
      reject(new Error('upload-aborted'));
    });

    xhr.open('PUT', presignedUrl);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.setRequestHeader('Content-Disposition', `inline;filename="${sanitizeFileName(file.name)}"`);
    signal?.addEventListener('abort', onSignalAbort, { once: true });
    xhr.send(file);
  });
}

// Only consider the upload "ready" once the variant the avatar UI actually renders
// (square_medium) has landed. Earlier variants (thumb) would let us PATCH /me and
// then render a broken image when Flexicon tries to read square_medium.
function hasRenderableImageVariant(attachment) {
  return Boolean(attachment?.variants?.square_medium);
}

function delay(ms, signal) {
  return new Promise(function delayExecutor(resolve, reject) {
    if (signal?.aborted) {
      reject(new Error('upload-aborted'));
      return;
    }
    const timeoutId = setTimeout(function delayResolve() {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timeoutId);
      reject(new Error('upload-aborted'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function pollAttachmentUntilProcessed(attachmentId, signal) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new Error('upload-aborted');
    const response = await apiGetAsUser(`/media_attachments/${attachmentId}`, { signal });
    const payload = await readEnvelope(response);
    if (hasRenderableImageVariant(payload?.media_attachment)) return payload.media_attachment;
    await delay(POLL_INTERVAL_MS, signal);
  }
  throw new Error('upload-processing-timeout');
}

export async function uploadUserFlexicon(file, { onProgress, onProcessing, signal } = {}) {
  const created = await createUserFlexiconAttachment(file, signal);
  await uploadFileToPresignedUrl(created.presigned_upload_url, file, onProgress, signal);
  onProcessing?.();
  return pollAttachmentUntilProcessed(created.id, signal);
}

export async function applyUserFlexiconImage(attachmentId, { signal } = {}) {
  const response = await apiPatchAsUser(
    '/me',
    {
      user: { flexicon_type: 'image', flexicon_id: attachmentId },
    },
    { signal },
  );
  const payload = await readEnvelope(response);
  return payload?.user ?? null;
}
